import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import type { WeeklyBriefMover } from "@/services/weeklyBriefService";
import { SectionLabel } from "./SectionLabel";
import SmartImage from "@/components/ui/SmartImage";
import { T } from "@/i18n/T";

const price = (n: number) =>
  n >= 1
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `$${n.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;

function MoverRow({ m }: { m: WeeklyBriefMover }) {
  const up = (m.change_7d ?? 0) >= 0;
  return (
    <div className="flex items-center gap-3 border-b border-border/60 py-2.5 last:border-0">
      {m.image ? (
        <SmartImage src={m.image} w={48} h={48} alt="" className="h-6 w-6 rounded-full" />
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{m.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{m.symbol}</div>
      </div>
      <div className="text-right">
        <div className="text-sm tabular-nums text-foreground">{price(m.price)}</div>
        <div className={`text-xs tabular-nums ${up ? "text-success" : "text-destructive"}`}>
          {up ? "+" : ""}
          {(m.change_7d ?? 0).toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

export function WinnersLosers({ winners, losers }: { winners: WeeklyBriefMover[]; losers: WeeklyBriefMover[] }) {
  const all = [...(winners ?? []), ...(losers ?? [])];
  if (!all.length) return null;

  const chartData = [...all]
    .sort((a, b) => (b.change_7d ?? 0) - (a.change_7d ?? 0))
    .map((m) => ({ symbol: m.symbol?.toUpperCase() ?? "", change: Number(m.change_7d ?? 0) }));

  return (
    <section className="mb-16">
      <SectionLabel><T k="auto.movers-7-day-5599" /></SectionLabel>
      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <T k="auto.weekly-performance-c408" />
          </div>
          <div style={{ height: Math.max(220, chartData.length * 30) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 8 }}>
                <XAxis
                  type="number"
                  tickFormatter={(v: number) => `${v > 0 ? "+" : ""}${v.toFixed(0)}%`}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="symbol"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <ReferenceLine x={0} stroke="hsl(var(--border))" />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number) => [`${v > 0 ? "+" : ""}${v.toFixed(2)}%`, "7d"]}
                />
                <Bar dataKey="change" radius={[3, 3, 3, 3]} barSize={14}>
                  {chartData.map((d) => (
                    <Cell
                      key={d.symbol}
                      fill={d.change >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-success"><T k="auto.winners-1d50" /></div>
            {(winners ?? []).map((m) => (
              <MoverRow key={m.id} m={m} />
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-destructive"><T k="auto.losers-6ce7" /></div>
            {(losers ?? []).map((m) => (
              <MoverRow key={m.id} m={m} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
